import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import VoiceAgent from './components/VoiceAgent';
import Home from './pages/Home';
import SpaceDetail from './pages/SpaceDetail';
import RoomsIndex from './pages/RoomsIndex';
import RoomDetail from './pages/RoomDetail';
import Designs from './pages/Designs';
import Controls from './pages/Controls';
import Docs from './pages/Docs';
import Jobs from './pages/Jobs';
import DropUpload from './pages/DropUpload';
import Login from './components/Login';
import { useAuth } from './AuthContext';

// App shell pattern: public routes render freely, admin routes are wrapped in
// RequireAuth, and every route body sits inside an ErrorBoundary keyed by
// pathname so one page crashing never takes down navigation.
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="cs-auth-wrap">Loading…</div>;
  if (!user) {
    return (
      <div className="cs-auth-wrap">
        <Login />
      </div>
    );
  }
  return children;
}

// Launched from an iOS/Android home-screen icon (standalone display mode).
// The manifest's start_url already points at /controls; this covers icons
// added before that change or platforms that ignore start_url.
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

export default function App() {
  const { user } = useAuth();
  const location = useLocation();

  // Home-screen app: the house controls ARE the homepage.
  if (isStandalone() && location.pathname === '/') {
    return <Navigate to="/controls" replace />;
  }

  // Public guest-upload route is reachable without signing in — checked before
  // anything else (no navbar, no auth) so contributors with a link can upload.
  const isDropRoute = /^\/u\//.test(window.location.pathname);
  if (isDropRoute) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/u/:token" element={<DropUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <>
      <Navbar />
      <ErrorBoundary key={location.pathname}>
        <Routes>
          {/* ---- Public (read-only when signed out; writes need an
               allowlisted account, enforced by Firestore rules) ---- */}
          <Route path="/" element={<Home />} />
          <Route path="/spaces/:id" element={<SpaceDetail />} />
          <Route path="/rooms" element={<RoomsIndex />} />
          <Route path="/rooms/:id" element={<RoomDetail />} />
          <Route path="/designs" element={<Designs />} />
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <div className="cs-auth-wrap"><Login /></div>}
          />

          {/* ---- Admin-only (sign-in required) ---- */}
          <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />
          {/* System documentation — describes house infra, so sign-in required. */}
          <Route path="/docs" element={<RequireAuth><Docs /></RequireAuth>} />
          {/* Smart-home controls: sign-in required AND the page itself checks
              the controls whitelist (Firestore rules enforce it server-side). */}
          <Route path="/controls" element={<RequireAuth><Controls /></RequireAuth>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>

      {/* Voice + text assistant — signed-in users only (the backend endpoints
          require a Firebase ID token; no point rendering it logged out). */}
      {user && <VoiceAgent />}
    </>
  );
}
